import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SearchSelect } from '../pages/review/reviewUi';
import { useToast } from '../bom/ui/Toast';
import { sbomApi, itemUrl, downloadAssessmentPdf, ruleMatches } from './sbomApi';

/**
 * Vulnerabilities on the Software BOM (v4.31.0) — the chips, the verdict pill and the Assess drawer
 * shared by the release tree, the catalog and the Vulnerabilities view. Design §7–8; the numbers are
 * the prototype's (https://claude.ai/artifact/Uds3TJtXF22RFCRGutZtMj, boards Vulnerabilities / Assess).
 */

export const JUSTIFICATIONS = [
  ['code_not_present', 'Code not present'],
  ['code_not_reachable', 'Code not reachable'],
  ['requires_configuration', 'Requires a configuration we do not use'],
  ['requires_dependency', 'Requires a dependency we do not ship'],
  ['requires_environment', 'Requires an environment we do not run in'],
  ['protected_by_compiler', 'Protected by compiler'],
  ['protected_at_runtime', 'Protected at runtime'],
  ['protected_at_perimeter', 'Protected at the perimeter'],
  ['protected_by_mitigating_control', 'Protected by a mitigating control'],
];
const RESPONSES = [
  ['', 'No response yet'],
  ['update', 'Update the package'],
  ['workaround_available', 'Workaround in place'],
  ['will_not_fix', 'Will not fix'],
  ['can_not_fix', 'Cannot fix'],
  ['rollback', 'Roll back'],
];
const CONTROL_TONE = { controlled: 'ok', accepted: 'ok', mitigated: 'warn', not_run: 'warn', unverified: 'warn', failed: 'bad', uncontrolled: 'bad' };
const TEST_TONE = { pass: 'ok', fail: 'bad' };

const sevLetter = (s) => (s === 'Unknown' ? '?' : s[0]);

/** Severity chips for one component. `checkedAt` tells "none known" from "not checked"; `checkable`
 *  false (no purl, or a type like pkg:generic that OSV.dev has no data for) is "Can't check". */
export function VulnChips({ vulns, checkedAt, checkable, onOpen, max = 4 }) {
  const [all, setAll] = useState(false);
  if (!vulns) return null;
  if (!vulns.length) {
    if (checkedAt) return <span className="sbom-muted sbom-tiny">None known</span>;
    return checkable === false
      ? <span className="sbom-muted sbom-tiny" title="This component has no package URL OSV.dev can look up (for example pkg:generic, or none at all), so it is never checked. Give it a pypi / npm / maven / deb … purl in the SBOM to have it checked.">Can't check</span>
      : <span className="sbom-muted sbom-tiny" title="Not checked yet — press Check now">Not checked</span>;
  }
  const shown = all ? vulns : vulns.slice(0, max);
  return (
    <span className="sbom-chips">
      {shown.map((v) => (
        <button type="button" key={v.id} className={`sbom-chip sbom-chip--${v.severity}`}
          title={`${v.id} · ${v.severity}${v.score != null ? ` ${v.score}` : ''}${v.kev ? ' · actively exploited (CISA KEV)' : ''}\n${v.summary || ''}\n${v.label}`}
          onClick={(e) => { e.stopPropagation(); onOpen?.(v); }}>
          {sevLetter(v.severity)}{v.score != null ? ` ${v.score}` : ''}{v.kev && <span className="sbom-kev">KEV</span>}
        </button>
      ))}
      {vulns.length > max && (
        <button type="button" className="sbom-chips-more"
          title={all ? 'Show only the first few' : `Show all ${vulns.length} vulnerabilities`}
          onClick={(e) => { e.stopPropagation(); setAll(!all); }}>
          {all ? 'Show less' : `+${vulns.length - max} more`}
        </button>
      )}
    </span>
  );
}

/** The most urgent verdict among a component's vulnerabilities. */
export function WorstVerdict({ vulns }) {
  if (!vulns || !vulns.length) return null;
  const w = [...vulns].sort((a, b) => b.rank - a.rank)[0];
  return <span className={`sbom-pill sbom-pill--${w.tone}`}>{w.label}</span>;
}

/**
 * The Assess drawer: the advisory, where it is, and the VEX verdict — Not affected with its reason, or
 * Affected linked to a risk from the Software BOM's Risk panel (its chain read from the Risk module).
 */
export function AssessDrawer({ vulnId, pkg, info, onClose, onSaved, onAuth }) {
  const { showToast } = useToast();
  const s = info.settings;
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [risks, setRisks] = useState(null);       // {items, suggestions, panel} | {error}
  const [chain, setChain] = useState(null);       // {risk, chains} | {loading} | {error}
  const [picking, setPicking] = useState(false);
  const [ai, setAi] = useState(null);             // {existing, new_risks, cached} | {loading} | {error}
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [toOrcanos, setToOrcanos] = useState(null);   // null = not touched: follows the rule

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let off = false;
    sbomApi.finding(vulnId, pkg)
      .then((x) => {
        if (off) return;
        setD(x);
        const a = x.assessment || {};
        setDraft({ status: a.status || 'investigating', justification: a.justification || '', response: a.response || '',
          detail: a.detail || '', mitigation_key: a.mitigation_key || '', risk_key: a.risk_key || '', risk_item_id: '' });
      })
      .catch((e) => { if (!off && !onAuth(e)) setError(e.message); });
    return () => { off = true; };
  }, [vulnId, pkg, onAuth]);

  const affected = draft?.status === 'affected';
  const riskPanel = !!d?.risk_panel;
  useEffect(() => {
    if (!affected || !riskPanel || risks) return;
    sbomApi.risks(vulnId, pkg)
      .then((x) => {
        setRisks(x);
        // A saved link is shown by its key; find the item to read its chain.
        setDraft((dr) => {
          if (!dr || dr.risk_item_id || !dr.risk_key) return dr;
          const hit = x.items.find((r) => r.key === dr.risk_key);
          return hit ? { ...dr, risk_item_id: hit.item_id } : dr;
        });
      })
      .catch((e) => { if (!onAuth(e)) setRisks({ error: e.message }); });
  }, [affected, riskPanel, risks, vulnId, pkg, onAuth]);

  const riskId = draft?.risk_item_id;
  useEffect(() => {
    if (!riskId) { setChain(null); return; }
    setChain({ loading: true });
    sbomApi.riskChain(riskId).then(setChain).catch((e) => { if (!onAuth(e)) setChain({ error: e.message }); });
  }, [riskId, onAuth]);

  // "Save to Orcanos" starts ticked when the finding already has an item, or when the saved rule
  // selects it with the verdict being chosen; the person decides.
  const ruleTick = !!d?.vuln_item && (!!d?.assessment?.orcanos_key
    || ruleMatches(d?.vuln_rule, { ...d, state: draft?.status }));
  const pushIt = d?.vuln_item ? (toOrcanos ?? ruleTick) : false;

  const riskById = useMemo(() => Object.fromEntries((risks?.items || []).map((r) => [String(r.item_id), r])), [risks]);
  const set = (patch) => setDraft((x) => ({ ...x, ...patch }));

  const save = async () => {
    if (draft.status === 'not_affected' && !draft.justification) { showToast('Choose why it does not affect the device.', 'error'); return; }
    if (draft.status === 'not_affected' && draft.justification === 'protected_by_mitigating_control' && !draft.mitigation_key.trim()) {
      showToast('Give the key of the mitigation that protects it.', 'error'); return;
    }
    if (affected && riskPanel && !draft.risk_item_id) { showToast('Link the risk this vulnerability creates.', 'error'); return; }
    setBusy(true);
    try {
      const r = await sbomApi.assess({
        vuln_id: vulnId, package_key: pkg, status: draft.status, justification: draft.status === 'not_affected' ? draft.justification : '',
        response: affected ? draft.response : '', detail: draft.detail,
        mitigation_key: draft.justification === 'protected_by_mitigating_control' ? draft.mitigation_key.trim() : '',
        risk_item_id: affected ? draft.risk_item_id : '',
        ...(d?.vuln_item ? { to_orcanos: pushIt } : {}),
      });
      if (r.orcanos_note) showToast(r.orcanos_note, 'info');
      else showToast(r.orcanos_written ? `Saved. ${r.assessment.orcanos_key} written to Orcanos.` : 'Saved.', 'success');
      onSaved?.(r);
      onClose();
    } catch (e) {
      if (!onAuth(e)) showToast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  // The PDF prints the SAVED assessment — unsaved edits in the drawer are not in it.
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadAssessmentPdf(vulnId, pkg);
    } catch (e) {
      if (!onAuth(e)) showToast(e.message, 'error');
    } finally {
      setPdfBusy(false);
    }
  };

  const compLink = (iid) => itemUrl(info.baseUrl, s.versionId, s.componentType, iid);
  const a = d?.assessment;
  const linked = riskById[String(draft?.risk_item_id || '')];
  const sugg = (risks?.suggestions || []).map((x) => ({ ...x, risk: riskById[String(x.item_id)] })).filter((x) => x.risk);
  const riskOptions = (risks?.items || []).map((r) => ({ value: String(r.item_id), label: `${r.key} · ${r.name}` }));
  const pickRisk = (id) => { set({ risk_item_id: String(id || '') }); setPicking(false); };
  // ✨ AI: a verdict on each word match, the panel's other fits, and new risks to write (billed per call).
  const aiByKey = Object.fromEntries((ai?.existing || []).map((m) => [m.key, m]));
  const suggIds = new Set(sugg.map((x) => String(x.item_id)));
  const aiMore = (ai?.existing || []).filter((m) => !suggIds.has(String(m.item_id)) && riskById[m.item_id] && m.fit >= 30);
  const runAi = async (force) => {
    setAi({ loading: true });
    try {
      setAi(await sbomApi.riskAi(vulnId, pkg, force));
    } catch (e) {
      if (!onAuth(e)) setAi({ error: e.message });
    }
  };
  const copyNew = (n) => {
    const t = `${n.name}\nHazard: ${n.hazard}\nHazardous situation: ${n.situation}\nHarm: ${n.harm}`;
    navigator.clipboard?.writeText(t).then(() => showToast('Copied. Paste it into a new risk in Risk Management.', 'success'),
      () => showToast('Could not copy.', 'error'));
  };

  return (
    <div className="sbom-overlay sbom-overlay--drawer" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="sbom-drawer" role="dialog" aria-modal="true" aria-label={`Assess ${vulnId}`}>
        <div className="sbom-modal-head">
          <div style={{ minWidth: 0 }}>
            <h3 className="sbom-mono" style={{ fontWeight: 500 }}>{vulnId}</h3>
            {d && (
              <div className="sbom-chips" style={{ marginTop: 6 }}>
                <span className={`sbom-chip sbom-chip--${d.severity}`}>{d.severity}{d.score != null && ` · CVSS ${d.score}`}</span>
                {d.kev && <span className="sbom-kev sbom-kev--lg" title={`On the CISA KEV list since ${d.kev}`}>KEV · actively exploited</span>}
                {a?.orcanos_key && <span className="sbom-tiny sbom-muted">Orcanos item <span className="sbom-mono">{a.orcanos_key}</span></span>}
              </div>
            )}
          </div>
          <button type="button" className="sbom-modal-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="sbom-modal-body sbom-drawer-body">
          {error && <div className="sbom-error" role="alert">{error}</div>}
          {!d && !error && <div className="sbom-muted">Loading…</div>}
          {d && draft && (
            <>
              <section>
                {d.summary && <p style={{ margin: '0 0 8px' }}>{d.summary}</p>}
                {d.details && d.details !== d.summary && (
                  <details className="sbom-tiny" style={{ marginBottom: 8 }}><summary>Full description</summary>
                    <div className="sbom-md">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}
                        components={{ a: ({ node, children, ...p }) => <a {...p} target="_blank" rel="noreferrer">{children}</a> }}>{d.details}</ReactMarkdown>
                    </div></details>
                )}
                <dl className="sbom-kv">
                  <dt>Package</dt>
                  <dd>{d.package} {d.versions.map((v) => (
                    <span key={v.item_id} className="sbom-mono">{v.version}{v.key && <> <a className="sbom-key" href={compLink(v.item_id)} target="_blank" rel="noreferrer">{v.key}</a></>} </span>
                  ))}</dd>
                  <dt>Fixed in</dt><dd className="sbom-mono">{d.fixed || <span className="sbom-muted">No fix published</span>}</dd>
                  <dt>Releases</dt>
                  <dd>{d.releases.map((r) => <span key={r.item_id} className="sbom-pill sbom-pill--plain sbom-rel-pill">{r.name}</span>)}
                    {d.fixed_in.length > 0 && <span className="sbom-pill sbom-pill--ok sbom-rel-pill" title="Releases that ship a version without it">
                      not in {d.fixed_in.map((r) => r.name).join(', ')}</span>}</dd>
                  <dt>Source</dt>
                  <dd className="sbom-tiny">
                    {d.osv_ids.map((x) => <a key={x} className="sbom-key" href={`https://osv.dev/vulnerability/${encodeURIComponent(x)}`} target="_blank" rel="noreferrer">{x}</a>)
                      .reduce((acc, el, i) => (i ? [...acc, ' · ', el] : [el]), [])}
                    {d.aliases.length > 0 && <span className="sbom-muted"> · also {d.aliases.slice(0, 4).join(', ')}</span>}
                  </dd>
                  <dt>First seen</dt><dd className="sbom-tiny">{String(d.first_seen || '').slice(0, 10)} UTC{d.published && <span className="sbom-muted"> · published {d.published}</span>}</dd>
                </dl>
              </section>

              <section>
                <h4 className="sbom-sec-h">Your assessment (VEX)</h4>
                <div className="sbom-seg sbom-seg--wide" role="group" aria-label="Assessment">
                  {[['investigating', 'Investigating'], ['not_affected', 'Not affected'], ['affected', 'Affected']].map(([k, l]) => (
                    <button type="button" key={k} className={draft.status === k ? 'on' : ''} disabled={!d.can_assess}
                      onClick={() => set({ status: k })}>{l}</button>
                  ))}
                </div>
              </section>

              {draft.status === 'not_affected' && (
                <section className="sbom-col">
                  <label className="sbom-field">Why it does not affect the device
                    <select className="sbom-select" value={draft.justification} disabled={!d.can_assess}
                      onChange={(e) => set({ justification: e.target.value })}>
                      <option value="">Choose a reason…</option>
                      {JUSTIFICATIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </label>
                  {draft.justification === 'protected_by_mitigating_control' && (
                    <label className="sbom-field">The mitigation that protects it (its Orcanos key)
                      <input className="sbom-input sbom-mono" value={draft.mitigation_key} placeholder="e.g. SR-1980" disabled={!d.can_assess}
                        onChange={(e) => set({ mitigation_key: e.target.value })} />
                    </label>
                  )}
                  <label className="sbom-field">Detail for the reviewer
                    <textarea className="sbom-input" rows={3} value={draft.detail} disabled={!d.can_assess} onChange={(e) => set({ detail: e.target.value })} />
                  </label>
                  <div className="sbom-note">No risk is needed. The reason goes into the VEX statement of every export.</div>
                </section>
              )}

              {affected && (
                <section className="sbom-col">
                  <h4 className="sbom-sec-h">Link a risk</h4>
                  {!riskPanel && (
                    <div className="sbom-note">No Risk panel is set for the Software BOM. An administrator sets it in Admin → Settings →
                      Software BOM; until then the verdict is saved without a risk.</div>
                  )}
                  {riskPanel && risks?.error && <div className="sbom-warn">{risks.error}</div>}
                  {riskPanel && !risks && <div className="sbom-muted sbom-tiny">Reading the Risk panel…</div>}
                  {riskPanel && risks?.items && (linked && !picking ? (
                    <>
                      <RiskChain chain={chain} panelId={risks.panel?.id} />
                      <div><button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => setPicking(true)}>Link a different risk</button></div>
                    </>
                  ) : (
                    <>
                      <div className="sbom-tiny sbom-muted">From the Risk panel <b>{risks.panel?.name}</b>. Several vulnerabilities usually share
                        one risk, written at the level of harm.</div>
                      {sugg.map((x) => {
                        const v = aiByKey[x.risk.key];
                        return (
                          <button type="button" key={x.item_id} className={`sbom-opt${String(draft.risk_item_id) === String(x.item_id) ? ' sbom-opt--on' : ''}`}
                            onClick={() => pickRisk(x.item_id)}>
                            <div style={{ minWidth: 0 }}>
                              <div><span className="sbom-mono">{x.risk.key}</span> {x.risk.name}</div>
                              <div className="sbom-tiny sbom-muted">{x.why}</div>
                              {v && <div className={`sbom-tiny sbom-ai-say${v.fit < 50 ? ' sbom-ai-say--weak' : ''}`}>✨ AI {v.fit}%: {v.reason}</div>}
                              {ai?.existing && !v && <div className="sbom-tiny sbom-ai-say sbom-ai-say--weak">✨ AI: not a match.</div>}
                            </div>
                            <span className="sbom-score" title="Words the vulnerability and the risk share — a hint, not a decision">{x.score}%</span>
                          </button>
                        );
                      })}
                      {risks.ai && !ai?.existing && (
                        <div>
                          <button type="button" className="sbom-btn" onClick={() => runAi(false)} disabled={ai?.loading}>
                            {ai?.loading ? 'Checking with AI…' : '✨ Check with AI'}</button>
                          <div className="sbom-tiny sbom-muted" style={{ marginTop: 4 }}>Checks these matches, looks through the whole Risk panel,
                            and suggests risks the file is missing.</div>
                        </div>
                      )}
                      {ai?.error && <div className="sbom-warn">{ai.error}</div>}
                      {ai?.existing && (
                        <>
                          <h5 className="sbom-ai-h">✨ AI also suggests</h5>
                          {aiMore.length ? aiMore.map((m) => (
                            <button type="button" key={m.item_id} className={`sbom-opt${String(draft.risk_item_id) === String(m.item_id) ? ' sbom-opt--on' : ''}`}
                              onClick={() => pickRisk(m.item_id)}>
                              <div style={{ minWidth: 0 }}>
                                <div><span className="sbom-mono">{m.key}</span> {riskById[m.item_id]?.name}</div>
                                <div className="sbom-tiny sbom-muted">{m.reason}</div>
                              </div>
                              <span className="sbom-score" title="The AI's fit — a hint, not a decision">{m.fit}%</span>
                            </button>
                          )) : <div className="sbom-tiny sbom-muted">No other risk in the panel fits.</div>}
                          {ai.new_risks.length > 0 && (
                            <>
                              <h5 className="sbom-ai-h">✨ New risks the file may be missing</h5>
                              {ai.new_risks.map((n, i) => (
                                <div key={i} className="sbom-opt sbom-opt--new">
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div><b>{n.name}</b></div>
                                    <dl className="sbom-kv sbom-tiny" style={{ gridTemplateColumns: '120px 1fr', marginTop: 4 }}>
                                      <dt>Hazard</dt><dd>{n.hazard}</dd>
                                      <dt>Situation</dt><dd>{n.situation}</dd>
                                      <dt>Harm</dt><dd>{n.harm}</dd>
                                    </dl>
                                    <div className="sbom-tiny sbom-muted" style={{ marginTop: 4 }}>{n.why}</div>
                                  </div>
                                  <button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => copyNew(n)}>Copy</button>
                                </div>
                              ))}
                            </>
                          )}
                          <div className="sbom-tiny sbom-muted">AI suggestions are hints: you choose the risk.{ai.cached ? ' (Saved answer.)' : ''}{' '}
                            <button type="button" className="sbom-linkbtn" onClick={() => runAi(true)}>Ask again</button></div>
                        </>
                      )}
                      <label className="sbom-field">{sugg.length ? 'Or search all risks' : 'Search the risks'}
                        <SearchSelect options={riskOptions} value={draft.risk_item_id} ariaLabel="Risk" placeholder="Search risks…"
                          onChange={(v) => pickRisk(v)} />
                      </label>
                      <div className="sbom-tiny sbom-muted">None fits? Create the risk in{' '}
                        <Link to={risks.panel?.id ? `/risk/${risks.panel.id}/studio` : '/risk'} target="_blank">Risk Management</Link>, then link it here.</div>
                    </>
                  ))}
                  <label className="sbom-field">Response
                    <select className="sbom-select" value={draft.response} disabled={!d.can_assess} onChange={(e) => set({ response: e.target.value })}>
                      {RESPONSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </label>
                  <label className="sbom-field">Detail for the reviewer
                    <textarea className="sbom-input" rows={3} value={draft.detail} disabled={!d.can_assess} onChange={(e) => set({ detail: e.target.value })} />
                  </label>
                </section>
              )}

              {draft.status === 'investigating' && (
                <div className="sbom-note">Pick <b>Not affected</b> with a reason, or <b>Affected</b> and link the risk it creates. Until then
                  it counts as <b>to assess</b> on the release.</div>
              )}
              {!d.can_assess && <div className="sbom-warn">Your Orcanos permissions do not allow editing a {s.componentLabel || 'component'},
                so you can read this assessment but not change it.</div>}

              {d.history?.length > 0 && (
                <details className="sbom-tiny">
                  <summary>History ({d.history.length})</summary>
                  <ul className="sbom-hist">
                    {d.history.map((h, i) => (
                      <li key={i}><span className="sbom-mono">{String(h.at || '').slice(0, 16)}</span> UTC · {h.by} ·{' '}
                        <b>{{ not_affected: 'Not affected', affected: 'Affected', investigating: 'Investigating' }[h.status] || h.status}</b>
                        {h.justification && ` · ${h.justification.replace(/_/g, ' ')}`}{h.risk_key && ` · ${h.risk_key}`}</li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
        <div className="sbom-modal-foot">
          {a?.updated_by && <span className="sbom-tiny sbom-muted" style={{ marginRight: 'auto', alignSelf: 'center' }}>
            Last saved by {a.updated_by}, {String(a.updated_at || '').slice(0, 10)}</span>}
          <button type="button" className="sbom-btn sbom-btn--ghost" onClick={exportPdf} disabled={pdfBusy || !d}
            title="Download this vulnerability and its saved assessment as a PDF">{pdfBusy ? 'Exporting…' : 'Export PDF'}</button>
          <button type="button" className="sbom-btn" onClick={onClose}>Cancel</button>
          {d?.vuln_item && (
            <label className="sbom-check" title={a?.orcanos_key ? `Also update ${a.orcanos_key} in Orcanos`
              : 'Also create a Vulnerability item in Orcanos, traced to the risk and the affected components'}>
              <input type="checkbox" checked={pushIt} onChange={(e) => setToOrcanos(e.target.checked)} />
              Save to Orcanos{a?.orcanos_key ? ` (${a.orcanos_key})` : ''}
            </label>
          )}
          <button type="button" className="sbom-btn sbom-btn--pri" onClick={save} disabled={busy || !d?.can_assess}>
            {busy ? 'Saving…' : 'Save'}</button>
        </div>
      </aside>
    </div>
  );
}

/** A risk → its mitigations → their tests, as the Risk module scores them (never a test on the risk). */
export function RiskChain({ chain, panelId }) {
  if (!chain || chain.loading) return <div className="sbom-muted sbom-tiny">Reading the risk…</div>;
  if (chain.error) return <div className="sbom-warn">{chain.error}</div>;
  const r = chain.risk;
  return (
    <div className="sbom-chain">
      <div className="sbom-cnode">
        <span className="sbom-cty">Risk</span>
        <span className="sbom-cnm"><span className="sbom-mono">{r.key}</span> {r.name}</span>
        <span className={`sbom-pill sbom-pill--${CONTROL_TONE[r.control.status] || 'plain'}`} title={r.control.hint}>{r.control.label || 'Not read'}</span>
      </div>
      {chain.chains.map((m) => (
        <React.Fragment key={m.key}>
          <div className="sbom-cnode sbom-ind1">
            <span className="sbom-cty">Mitigation</span>
            <span className="sbom-cnm"><span className="sbom-mono">{m.key}</span> {m.name}</span>
          </div>
          {m.tests.length ? m.tests.map((t) => (
            <div key={t.key} className="sbom-cnode sbom-ind2">
              <span className="sbom-cty">Test</span>
              <span className="sbom-cnm"><span className="sbom-mono">{t.key}</span> {t.name}</span>
              <span className={`sbom-pill sbom-pill--${TEST_TONE[t.result] || 'warn'}`}>{t.result_text || { pass: 'Pass', fail: 'Fail' }[t.result] || 'Not run'}</span>
            </div>
          )) : (
            <div className="sbom-cnode sbom-ind2">
              <span className="sbom-cty">Test</span>
              <span className="sbom-cnm sbom-muted">{m.tests_known ? 'No test yet. The mitigation is not verified.' : 'Tests not read for this panel.'}</span>
            </div>
          )}
        </React.Fragment>
      ))}
      {!chain.chains.length && (
        <div className="sbom-cnode sbom-ind1"><span className="sbom-cty">Mitigation</span>
          <span className="sbom-cnm sbom-muted">None yet. The risk is uncontrolled.</span></div>
      )}
      {panelId && (
        <div className="sbom-ind1 sbom-tiny">
          <Link to={`/risk/${panelId}/r/${r.item_id}`} target="_blank">Open in Risk Management</Link>
          <span className="sbom-muted"> to add a mitigation or link its test.</span>
        </div>
      )}
    </div>
  );
}
